import api from '@api/index'
import { SimulationStatus } from '@enums/SimulationStatus'
import { SimulationDTO } from '@models/api/simulation/SimulationDTO'
import { SimulationListItemDTO } from '@models/api/simulation/SimulationListItemDTO'
import { Simulation } from '@models/Simulation'
import dayjs, { Dayjs } from 'dayjs'

export class SimulationListItem {
  initDate!: Dayjs
  creationDate!: Dayjs

  private constructor(
    public id: string,
    public name: string,
    public status: SimulationListItemDTO['STATUS']
  ) {}

  static fromDTO(data: SimulationListItemDTO): SimulationListItem {
    const self = new this(data.ID_SIMULATION, data.NAME, data.STATUS)
    self.initDate = dayjs(data.INIT_TIME)
    self.creationDate = dayjs(data.CREATION_TIME)
    return self
  }

  static fromSimulationDTO(dto: SimulationDTO): SimulationListItem {
    const self = new this(dto.ID_SIMULATION, dto.NAME, dto.STATUS)
    self.initDate = dayjs(dto.INIT_TIME)
    self.creationDate = dayjs(dto.CREATION_TIME)
    return self
  }

  toPartialSimulationDTO() {
    return {
      ID_SIMULATION: this.id,
      NAME: this.name,
      STATUS: this.status,
      INIT_TIME: this.initDate.toISOString(),
      CREATION_TIME: this.creationDate.toISOString()
    }
  }

  static fromSimulation(simulation: Simulation): SimulationListItem {
    const self = new this(
      simulation.simulationListItem.id,
      simulation.simulationListItem.name,
      simulation.simulationListItem.status
    )
    self.initDate = simulation.initDate
    self.creationDate = dayjs(simulation.simulationListItem.creationDate)
    return self
  }

  async fetchSimulation() {
    const simulationDTO = (await api.simulation.getSimulationData(this.id)).data
    const simulation = Simulation.fromDTO(simulationDTO)
    this.updateWithSimulationListItem(simulation.simulationListItem)
    return simulation
  }

  updateWithSimulationListItem(simulationListItem: SimulationListItem) {
    this.status = simulationListItem.status
    this.initDate = simulationListItem.initDate
    this.creationDate = simulationListItem.creationDate
    this.name = simulationListItem.name
    this.id = simulationListItem.id
  }

  canDownloadFiles() {
    return this.hasSomeStatus([
      SimulationStatus.COMPLETED,
      SimulationStatus.ERROR_IGP,
      SimulationStatus.ERROR_REPORT_IGP,
      SimulationStatus.ERROR_REPORT
    ])
  }

  canShowCharts() {
    return this.hasSomeStatus([
      SimulationStatus.COMPLETED,
      SimulationStatus.ERROR_IGP
    ])
  }

  isMapSimulationCompleted(): boolean {
    return (
      this.status === SimulationStatus.COMPLETED ||
      this.status === SimulationStatus.POSTPROCESSING ||
      this.status === SimulationStatus.ERROR_IGP ||
      this.status === SimulationStatus.ERROR_REPORT
    )
  }

  isErrorOnGenerateReport(): boolean {
    return (
      this.status === SimulationStatus.ERROR_REPORT ||
      this.status === SimulationStatus.ERROR_REPORT_IGP
    )
  }

  isErrorOnGenerateFiles(): boolean {
    return (
      this.status === SimulationStatus.ERROR_REPORT ||
      this.status === SimulationStatus.ERROR_IGP
    )
  }

  canDownloadReport() {
    return this.hasSomeStatus([
      SimulationStatus.COMPLETED,
      SimulationStatus.ERROR_IGP
    ])
  }

  canDownloadIGP() {
    return this.hasSomeStatus([
      SimulationStatus.COMPLETED,
      SimulationStatus.ERROR_REPORT
    ])
  }

  hasSomeStatus(statuses: SimulationStatus[]) {
    return statuses.includes(this.status)
  }

  canDeleteSimulation() {
    return this.hasSomeStatus([
      SimulationStatus.COMPLETED,
      SimulationStatus.ERROR_IGP,
      SimulationStatus.ERROR_REPORT,
      SimulationStatus.ERROR_REPORT_IGP,
      SimulationStatus.ERROR
    ])
  }
}
